export type PlanItemKind = "lesson" | "quiz" | "presentation";

export interface PlanItemCountable {
  itemKind?: PlanItemKind;
}

export type PlanItemCounts = {
  lessons: number;
  quizzes: number;
  presentations: number;
  total: number;
};

/** Teacher progress views count lessons and quizzes, not slide decks. */
export function isLessonOrQuizProgressItem<T extends PlanItemCountable>(item: T): boolean {
  return item.itemKind !== "presentation";
}

export function lessonAndQuizItems<T extends PlanItemCountable>(items: readonly T[]): T[] {
  return items.filter(isLessonOrQuizProgressItem);
}

export function countPlanItems<T extends PlanItemCountable>(items: readonly T[]): PlanItemCounts {
  let lessons = 0;
  let quizzes = 0;
  let presentations = 0;
  for (const item of items) {
    if (item.itemKind === "quiz") quizzes += 1;
    else if (item.itemKind === "presentation") presentations += 1;
    else lessons += 1;
  }
  return { lessons, quizzes, presentations, total: lessons + quizzes + presentations };
}

export function formatPlanItemCounts(counts: PlanItemCounts): string {
  const parts: string[] = [];
  if (counts.lessons > 0) {
    parts.push(`${counts.lessons} lesson${counts.lessons === 1 ? "" : "s"}`);
  }
  if (counts.quizzes > 0) {
    parts.push(`${counts.quizzes} quiz${counts.quizzes === 1 ? "" : "es"}`);
  }
  if (counts.presentations > 0) {
    parts.push(`${counts.presentations} presentation${counts.presentations === 1 ? "" : "s"}`);
  }
  if (parts.length === 0) return "0 assignments";
  return parts.join(", ");
}

export function assignmentColumnHeader(title: string, itemKind?: PlanItemKind): string {
  const kind =
    itemKind === "quiz" ? "Quiz" : itemKind === "presentation" ? "Presentation" : "Lesson";
  return `${kind}: ${title}`;
}
